import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchTokens,
  createToken,
  updateToken,
  deleteToken,
  regenerateToken,
} from "./thunks";
import { TokensState, Token } from "./types";

const initialState: TokensState = {
  tokens: [],
  pagination: {
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  },
  loading: false,
  error: null,
  selectedEstablishmentId: null,
};

const tokensSlice = createSlice({
  name: "tokens",
  initialState,
  reducers: {
    clearTokens: (state) => {
      state.tokens = [];
      state.pagination = {
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      };
      state.loading = false;
      state.error = null;
      state.selectedEstablishmentId = null;
    },
    setSelectedEstablishment: (state, action: PayloadAction<number>) => {
      state.selectedEstablishmentId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tokens cases
      .addCase(fetchTokens.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTokens.fulfilled, (state, action) => {
        state.loading = false;
        const { data, establishmentId } = action.payload;

        state.selectedEstablishmentId = establishmentId;

        if (data && data.data) {
          state.tokens = data.data;
          state.pagination = {
            current_page: data.current_page,
            per_page: data.per_page,
            total: data.total,
            last_page: data.last_page,
          };
        } else {
          state.tokens = [];
          state.pagination = {
            current_page: 1,
            per_page: 15,
            total: 0,
            last_page: 1,
          };
        }
      })
      .addCase(fetchTokens.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao carregar tokens";
      })

      // Create token cases
      .addCase(createToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createToken.fulfilled, (state, action) => {
        state.loading = false;
        const newToken = action.payload.data;

        // Adicionar novo token à lista
        state.tokens.unshift(newToken);
        state.pagination.total += 1;
      })
      .addCase(createToken.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error || "Erro ao criar token";
      })

      // Update token cases
      .addCase(updateToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateToken.fulfilled, (state, action) => {
        state.loading = false;
        const updatedToken = action.payload.data;

        // Atualizar token na lista
        const index = state.tokens.findIndex(
          (token) => token.id === updatedToken.id,
        );
        if (index !== -1) {
          state.tokens[index] = updatedToken;
        }
      })
      .addCase(updateToken.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao atualizar token";
      })

      // Delete token cases
      .addCase(deleteToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteToken.fulfilled, (state, action) => {
        state.loading = false;
        const { tokenId } = action.payload;

        // Remover token da lista
        state.tokens = state.tokens.filter((token) => token.id !== tokenId);
        state.pagination.total -= 1;
      })
      .addCase(deleteToken.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao excluir token";
      })

      // Regenerate token cases
      .addCase(regenerateToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(regenerateToken.fulfilled, (state, action) => {
        state.loading = false;
        const regeneratedToken = action.payload.data;

        // Atualizar token regenerado na lista
        const index = state.tokens.findIndex(
          (token) => token.id === regeneratedToken.id,
        );
        if (index !== -1) {
          state.tokens[index] = regeneratedToken;
        }
      })
      .addCase(regenerateToken.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao regenerar token";
      });
  },
});

export const { clearTokens, setSelectedEstablishment } = tokensSlice.actions;
export default tokensSlice.reducer;
